import { useState, useEffect } from 'react';
import { db } from '../utils/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { useAuth } from './useAuth';

// Days of week
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' }
];

// Time block categories/types - Rich colors for light mode
export const BLOCK_TYPES = {
  FOCUS: { id: 'focus', label: 'Focus Work', color: '#5B6BD4' },
  MEETING: { id: 'meeting', label: 'Meetings', color: '#2D8A5F' },
  ADMIN: { id: 'admin', label: 'Admin', color: '#C45D3A' },
  COMMUNICATION: { id: 'communication', label: 'Communication', color: '#C44D7A' },
  PLANNING: { id: 'planning', label: 'Planning', color: '#7C5CBF' },
  BREAK: { id: 'break', label: 'Break', color: '#7A7A7A' },
  OTHER: { id: 'other', label: 'Other', color: '#3B82C4' }
};

export function useTimeBlocks() {
  const { user } = useAuth();
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Time blocks are private - only show current user's blocks
    if (!user?.uid) {
      setTimeBlocks([]);
      setLoading(false);
      return;
    }

    // Query by userId (new multi-tenant field)
    const q = query(
      collection(db, 'timeBlocks'),
      where('userId', '==', user.uid),
      orderBy('dayOfWeek', 'asc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const blocks = snap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }));
        setTimeBlocks(blocks);
        setLoading(false);
      },
      (err) => {
        console.error('🔥 [useTimeBlocks] listener error:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  const addTimeBlock = async (blockData) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add time blocks');
    }

    await addDoc(collection(db, 'timeBlocks'), {
      ...blockData,
      userId: user.uid, // Multi-tenant: owner by Firebase UID
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  const updateTimeBlock = async (id, updates) => {
    await updateDoc(doc(db, 'timeBlocks', id), {
      ...updates,
      updatedAt: serverTimestamp()
    });
  };

  const deleteTimeBlock = async (id) => {
    await deleteDoc(doc(db, 'timeBlocks', id));
  };

  // Get blocks for a specific day
  const getBlocksForDay = (dayOfWeek) => {
    return timeBlocks
      .filter((b) => b.dayOfWeek === dayOfWeek)
      .sort((a, b) => {
        // Sort by start time
        const aTime = parseTime(a.startTime);
        const bTime = parseTime(b.startTime);
        return aTime - bTime;
      });
  };

  // Get blocks for today
  const getTodayBlocks = () => {
    const today = new Date().getDay();
    return getBlocksForDay(today);
  };

  // Get recurring blocks (those that repeat weekly)
  const getRecurringBlocks = () => {
    return timeBlocks.filter((b) => b.recurring);
  };

  // Find which time block a task should go into based on context
  const findBlockForContext = (context, dayOfWeek = new Date().getDay()) => {
    const dayBlocks = getBlocksForDay(dayOfWeek);

    // Map contexts to block types
    const contextToBlockType = {
      '@Slack': 'communication',
      '@Email': 'communication',
      '@Calls': 'meeting',
      '@Computer': 'focus',
      '@Errands': 'other',
      '@Home': 'other',
      '@Office': 'focus',
      '@Agenda': 'meeting'
    };

    const targetType = contextToBlockType[context];
    if (targetType) {
      return dayBlocks.find((b) => b.type === targetType);
    }
    return null;
  };

  return {
    timeBlocks,
    loading,
    addTimeBlock,
    updateTimeBlock,
    deleteTimeBlock,
    getBlocksForDay,
    getTodayBlocks,
    getRecurringBlocks,
    findBlockForContext
  };
}

// Helper to parse time string to minutes from midnight
function parseTime(timeStr) {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

// Helper to format time for display
export function formatTime(timeStr) {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Calculate duration between two times
export function calculateDuration(startTime, endTime) {
  const start = parseTime(startTime);
  const end = parseTime(endTime);
  const durationMinutes = end - start;

  if (durationMinutes < 60) {
    return `${durationMinutes}m`;
  }

  const hours = Math.floor(durationMinutes / 60);
  const mins = durationMinutes % 60;

  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}m`;
}
